import React from 'react';
import { Button } from './ui/button';
import { Plus, Check } from 'lucide-react';
import { useInviteCart } from './InviteCartProvider';
import { useAuth } from '../context/AuthContext';

/**
 * Iter-187 — Reusable +/Cart toggle. Renders only for admin role; a no-op
 * for other roles (so surfaces shared with clients/talent don't render
 * an admin-only chip).
 *
 * Props:
 *   talent: { user_id, name|display_name|first_name, photo_url|profile_photo_url, role|role_name, city, state }
 *   size?: 'xs' | 'sm' (default 'sm')
 *   variant?: 'icon' | 'pill' (default 'pill')
 *   className?: string
 *   testIdSuffix?: string (used in data-testid=`add-to-cart-${suffix}`)
 *   onToggle?: (added: boolean) => void
 */
const AddToInviteCartButton = ({
  talent,
  size = 'sm',
  variant = 'pill',
  className = '',
  testIdSuffix,
  onToggle,
}) => {
  const { user } = useAuth();
  const { add, remove, has } = useInviteCart();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  if (!isAdmin || !talent?.user_id) return null;

  const inCart = has(talent.user_id);
  const tid = testIdSuffix || talent.user_id;

  const onClick = (e) => {
    e?.stopPropagation?.();
    if (inCart) {
      remove(talent.user_id);
      onToggle?.(false);
    } else {
      add(talent);
      onToggle?.(true);
    }
  };

  if (variant === 'icon') {
    return (
      <Button
        type="button"
        size="icon"
        variant={inCart ? 'default' : 'outline'}
        onClick={onClick}
        className={`h-8 w-8 shrink-0 ${inCart ? 'bg-cyan-500 hover:bg-cyan-600 text-white border-cyan-500' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'} ${className}`}
        data-testid={`add-to-cart-${tid}`}
        title={inCart ? 'Remove from Invite List' : 'Add to Invite List'}
        aria-label={inCart ? 'Remove from Invite List' : 'Add to Invite List'}
      >
        {inCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      size={size}
      variant={inCart ? 'default' : 'outline'}
      onClick={onClick}
      className={`${inCart ? 'bg-cyan-500 hover:bg-cyan-600 text-white border-cyan-500' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'} text-xs ${className}`}
      data-testid={`add-to-cart-${tid}`}
    >
      {inCart ? (
        <><Check className="w-3.5 h-3.5 mr-1" /> In list</>
      ) : (
        <><Plus className="w-3.5 h-3.5 mr-1" /> Add to Invite List</>
      )}
    </Button>
  );
};

export default AddToInviteCartButton;
